/**
 * AdminAcceptInvite — ADR-0022 admin scope.
 *
 * Konsumuje zaproszenie admina. URL z maila:
 *   `https://admin.<base>/accept-invite/:token`
 * Token czytany z params, POST body: { password }.
 *
 * Brak displayName/tenantSlug w body — admin scope jest globalny, displayName
 * ustawia super-admin przy invite (lub admin sam pozniej w /profile).
 *
 * Stany:
 *  - 'idle' — formularz aktywny.
 *  - 'submitting' — request w locie.
 *  - 'expired' — backend zwrocil 410 INVALID_OR_EXPIRED → ekran „Zaproszenie wygaslo".
 *
 * Po sukcesie → /login z toastem (admin musi sie zalogowac normalnie, w tym 2FA setup).
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

type State = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'expired' };

interface BackendError {
  error?: string;
  details?: string[] | Record<string, string[]>;
}

function extractError(err: unknown): { status: number | null; data: BackendError | null } {
  const e = err as { response?: { status?: number; data?: BackendError } };
  return {
    status: e.response?.status ?? null,
    data: e.response?.data ?? null,
  };
}

function policyErrorsToMessages(details: string[] | undefined, t: (k: string) => string): string[] {
  if (!details) return [];
  return details.map((code) => {
    switch (code) {
      case 'TOO_SHORT':
        return t('auth.adminAccept.policy.tooShort');
      case 'TOO_LONG':
        return t('auth.adminAccept.policy.tooLong');
      case 'NO_DIGIT':
        return t('auth.adminAccept.policy.noDigit');
      case 'NO_UPPER':
        return t('auth.adminAccept.policy.noUpper');
      case 'NO_LOWER':
        return t('auth.adminAccept.policy.noLower');
      case 'NO_SPECIAL':
        return t('auth.adminAccept.policy.noSpecial');
      default:
        return code;
    }
  });
}

export function AdminAcceptInvite() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { admin, loading, logout } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [policyErrors, setPolicyErrors] = useState<string[]>([]);
  const [mismatchError, setMismatchError] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'expired' });
    }
  }, [token]);

  useEffect(() => {
    if (!loading && admin !== null) {
      logout().catch(() => navigate('/login', { replace: true }));
    }
  }, [loading, admin, logout, navigate]);

  if (loading || admin !== null) return null;

  const submitting = state.kind === 'submitting';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPolicyErrors([]);
    setMismatchError(false);

    if (password !== confirm) {
      setMismatchError(true);
      return;
    }

    if (!token) {
      setState({ kind: 'expired' });
      return;
    }

    setState({ kind: 'submitting' });

    try {
      await api.post(`/admin/accept-invite/${token}`, { password });
      toast.show({
        variant: 'success',
        title: t('auth.adminAccept.successTitle'),
        description: t('auth.adminAccept.successDescription'),
      });
      navigate('/login', { replace: true });
    } catch (err) {
      const { status, data } = extractError(err);
      if (status === 410) {
        setState({ kind: 'expired' });
        return;
      }
      if (status === 422 && data?.error === 'PASSWORD_POLICY_VIOLATION') {
        const details = Array.isArray(data.details) ? data.details : [];
        setPolicyErrors(policyErrorsToMessages(details, t));
        setState({ kind: 'idle' });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('auth.adminAccept.errors.genericTitle'),
        description: t('auth.adminAccept.errors.genericDescription'),
      });
      setState({ kind: 'idle' });
    }
  };

  if (state.kind === 'expired') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div
          className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl"
          data-testid="admin-accept-invite-expired"
        >
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-text-primary">
              {t('auth.adminAccept.expiredTitle')}
            </h1>
            <p className="mt-3 text-sm text-text-tertiary">
              {t('auth.adminAccept.expiredDescription')}
            </p>
          </div>
          <div className="mt-8">
            <Link to="/login">
              <Button variant="secondary" className="w-full">
                {t('auth.adminAccept.backToLogin')}
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary">{t('auth.adminAccept.title')}</h1>
          <p className="mt-2 text-sm text-text-tertiary">{t('auth.adminAccept.subtitle')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label={t('auth.adminAccept.passwordLabel')}
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            data-testid="admin-accept-invite-password"
          />
          <Input
            label={t('auth.adminAccept.confirmLabel')}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
            data-testid="admin-accept-invite-confirm"
          />

          <p className="text-xs text-text-tertiary">{t('auth.adminAccept.policyHint')}</p>

          {mismatchError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="admin-accept-invite-mismatch"
            >
              {t('auth.adminAccept.errors.mismatch')}
            </div>
          )}

          {policyErrors.length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="admin-accept-invite-policy-errors"
            >
              <ul className="list-inside list-disc space-y-1">
                {policyErrors.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            loading={submitting}
            data-testid="admin-accept-invite-submit"
          >
            {submitting ? t('auth.adminAccept.submitting') : t('auth.adminAccept.submit')}
          </Button>
        </form>
      </div>
    </main>
  );
}
